/**
 * Horizon 4.0 PDP runtime. Vanilla JS, no dependencies.
 * Forms keep working as plain POST when this file never loads.
 */
(function () {
  'use strict';

  var MODEL_VIEWER_SRC = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.2.0/model-viewer.min.js';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  // theme.js owns the shared live region; this file is deferred ahead of it,
  // so only call through once the runtime spine has registered.
  function announce(message) {
    if (window.Horizon && window.Horizon.announce) window.Horizon.announce(message);
  }

  ready(function () {
    var root = document.querySelector('[data-pdp]');
    if (root) {
      initGalleryCounter(root);
      initClicks(root);
      initVariantSelect(root);
      initVariantMedia(root);
      initAjaxCart(root);
      initStickyBar(root);
    }
    initFacades();
    initReviewsFacade();
    initRecommendations();
  });

  /* ---------- Mobile "N / M" gallery counter ---------- */
  function initGalleryCounter(root) {
    var counter = root.querySelector('[data-gallery-counter]');
    var gallery = root.querySelector('[data-gallery]');
    var items = root.querySelectorAll('[data-gallery-item]');
    if (!counter || !gallery || items.length < 2 || !('IntersectionObserver' in window)) return;
    counter.hidden = false;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var i = Array.prototype.indexOf.call(items, entry.target);
          counter.textContent = i + 1 + ' / ' + items.length;
        });
      },
      { root: gallery, threshold: 0.6 }
    );
    items.forEach(function (item) {
      io.observe(item);
    });
  }

  /* ---------- Delegated clicks: media lightbox, zoom, quantity ---------- */
  function resetLightbox(dialog) {
    if (!dialog) return;
    var frame = dialog.querySelector('[data-lightbox-frame]');
    if (frame) {
      frame.querySelectorAll('video, iframe, model-viewer').forEach(function (el) {
        if (el.tagName === 'VIDEO') {
          el.pause();
        }
        el.remove();
      });
    }
    var img = dialog.querySelector('[data-zoom-img]');
    if (img) {
      img.hidden = false;
      img.removeAttribute('src');
    }
    dialog.classList.remove('is-video');
  }

  function openVideoLightbox(dialog, item) {
    var frame = dialog.querySelector('[data-lightbox-frame]');
    var tpl = item.querySelector('template[data-media-template]');
    var img = dialog.querySelector('[data-zoom-img]');
    if (!frame || !tpl || !dialog.showModal) return;
    resetLightbox(dialog);
    if (img) img.hidden = true;
    dialog.classList.add('is-video');
    frame.appendChild(tpl.content.cloneNode(true));
    var video = frame.querySelector('video');
    if (video) {
      video.controls = true;
      video.setAttribute('playsinline', '');
      video.play().catch(function () {});
    }
    var iframe = frame.querySelector('iframe');
    if (iframe) {
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
      );
      try {
        var src = new URL(iframe.getAttribute('src') || '', window.location.href);
        if (!src.searchParams.has('autoplay')) src.searchParams.set('autoplay', '1');
        if (src.hostname.indexOf('youtube') !== -1) src.searchParams.set('rel', '0');
        iframe.src = src.toString();
      } catch (e) {
        /* keep the Shopify-rendered src */
      }
    }
    dialog.showModal();
  }

  function initClicks(root) {
    var dialog = document.querySelector('[data-zoom-dialog]');
    var desktop = window.matchMedia('(min-width: 750px)');
    var zoomItems = root.querySelectorAll('[data-gallery-item][data-zoom-src]');
    var zoomReturn = null;

    function openZoom(item) {
      if (!dialog || !dialog.showModal || !desktop.matches) return;
      var img = dialog.querySelector('[data-zoom-img]');
      if (!img) return;
      resetLightbox(dialog);
      img.src = item.getAttribute('data-zoom-src');
      img.alt = item.getAttribute('data-zoom-alt') || '';
      img.hidden = false;
      zoomReturn = item;
      dialog.showModal();
    }

    // Zoom tiles are plain <div>s in sections/product.liquid. Expose button
    // semantics only at the widths where the lightbox actually opens.
    function syncZoomRoles() {
      var interactive = !!(dialog && dialog.showModal && desktop.matches);
      zoomItems.forEach(function (item, i) {
        if (interactive) {
          item.setAttribute('role', 'button');
          item.setAttribute('tabindex', '0');
          item.setAttribute('aria-label', 'Zoom image ' + (i + 1));
        } else {
          item.removeAttribute('role');
          item.removeAttribute('tabindex');
          item.removeAttribute('aria-label');
        }
      });
    }

    zoomItems.forEach(function (item) {
      item.addEventListener('keydown', function (ev) {
        if (ev.key !== 'Enter' && ev.key !== ' ' && ev.key !== 'Spacebar') return;
        ev.preventDefault();
        openZoom(item);
      });
    });
    syncZoomRoles();
    if (desktop.addEventListener) desktop.addEventListener('change', syncZoomRoles);

    root.addEventListener('click', function (ev) {
      var play = ev.target.closest('[data-media-play]');
      if (play) {
        var item = play.closest('[data-gallery-item]');
        if (!item) return;
        var mediaType = item.getAttribute('data-media-type');
        if ((mediaType === 'video' || mediaType === 'external_video') && dialog) {
          openVideoLightbox(dialog, item);
          return;
        }
        var tpl = item.querySelector('template[data-media-template]');
        if (tpl) {
          item.innerHTML = '';
          item.appendChild(tpl.content.cloneNode(true));
          if (item.querySelector('model-viewer') && !window.customElements.get('model-viewer')) {
            var s = document.createElement('script');
            s.type = 'module';
            s.src = MODEL_VIEWER_SRC;
            document.head.appendChild(s);
          }
        }
        return;
      }

      var step = ev.target.closest('[data-qty-change]');
      if (step) {
        var input = step.parentElement.querySelector('input[name="quantity"]');
        if (!input) return;
        var delta = step.getAttribute('data-qty-change') === '+' ? 1 : -1;
        var min = parseInt(input.min, 10) || 1;
        var max = input.max ? parseInt(input.max, 10) : Infinity;
        input.value = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 1) + delta));
        return;
      }

      var zoomItem = ev.target.closest('[data-gallery-item][data-zoom-src]');
      if (zoomItem) openZoom(zoomItem);
    });

    if (dialog) {
      dialog.addEventListener('click', function (ev) {
        if (ev.target.closest('[data-zoom-close]')) {
          dialog.close();
          return;
        }
        // The dialog spans the full viewport, so a "backdrop-only" check is
        // unreachable. Image zoom: any click closes. Video: clicks on the
        // player must keep working, so only clicks outside it close.
        if (!dialog.classList.contains('is-video') || !ev.target.closest('video, iframe')) {
          dialog.close();
        }
      });
      dialog.addEventListener('close', function () {
        resetLightbox(dialog);
        // Return focus to the tile the lightbox was opened from (tabindex is
        // only present while the tile is keyboard-operable).
        if (zoomReturn && zoomReturn.getAttribute('tabindex') !== null && document.contains(zoomReturn)) {
          zoomReturn.focus();
        }
        zoomReturn = null;
      });
    }
  }

  /* ---------- Native variant select ---------- */
  function initVariantSelect(root) {
    var select = root.querySelector('[data-variant-select]');
    var dataEl = root.querySelector('[data-variant-json]');
    if (!select || !dataEl) return;
    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    select.addEventListener('change', function () {
      var v = variants.find(function (x) {
        return String(x.id) === select.value;
      });
      if (!v) return;

      var priceEl = root.querySelector('[data-price]');
      if (priceEl) {
        priceEl.innerHTML =
          '<span class="price">' +
          v.price +
          '</span>' +
          (v.compare_at_price ? ' <s class="compare-at-price">' + v.compare_at_price + '</s>' : '');
      }

      var skuEl = root.querySelector('[data-sku]');
      if (skuEl) skuEl.textContent = v.sku || '';

      document.querySelectorAll('[data-atc]').forEach(function (btn) {
        btn.disabled = !v.available;
        btn.textContent = v.available
          ? btn.getAttribute('data-text-add')
          : btn.getAttribute('data-text-sold-out');
      });

      var sticky = document.querySelector('[data-sticky-price]');
      if (sticky) sticky.textContent = v.price;

      var qty = root.querySelector('input[name="quantity"]');
      if (qty) {
        if (v.qty_max != null && v.qty_max > 0) qty.max = v.qty_max;
        else qty.removeAttribute('max');
      }

      var url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    });
  }

  /* ---------- Gallery follows the selected variant ---------- */
  /* The configurator app block owns variant choice on the live PDP. Its
     selectVariant() writes input[name="id"] (change event) and, after its own
     page fetch, dispatches variant:change on document with detail.variant.id.
     Its HTML swap targets Horizon 3 ids ([id*="ProductInformation-"]) that 4.0
     never renders, so the theme moves the variant's tile itself. */
  function initVariantMedia(root) {
    var gallery = root.querySelector('[data-gallery]');
    var mapEl = root.querySelector('[data-variant-media]');
    if (!gallery || !mapEl) return;
    var map;
    try {
      map = JSON.parse(mapEl.textContent);
    } catch (e) {
      return;
    }
    var phone = window.matchMedia('(max-width: 749.98px)');

    function showVariantMedia(variantId) {
      var mediaId = map[String(variantId)];
      if (!mediaId) return;
      var item = gallery.querySelector('[data-gallery-item][data-media-id="' + mediaId + '"]');
      if (!item) return;
      if (phone.matches) {
        // Carousel: bring the tile into view; the order (and "N / M") stays honest.
        item.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
        return;
      }
      // Grid: the first tile spans both columns, so the chosen color becomes the hero.
      if (item !== gallery.firstElementChild) gallery.prepend(item);
    }

    document.addEventListener('variant:change', function (ev) {
      var d = ev.detail || {};
      var id = d.variant && d.variant.id != null ? d.variant.id : d.variantId;
      if (id) showVariantMedia(id);
    });
    root.addEventListener('change', function (ev) {
      var t = ev.target;
      if (t && t.name === 'id' && t.value) showVariantMedia(t.value);
    });
  }

  /* ---------- AJAX add-to-cart ---------- */
  function initAjaxCart(root) {
    var form = root.querySelector('form[data-product-form]');
    if (!form || !window.fetch) return;
    var errorEl = form.querySelector('[data-atc-error]');

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var buttons = document.querySelectorAll('[data-atc]');
      buttons.forEach(function (b) {
        b.classList.add('is-loading');
      });
      if (errorEl) errorEl.hidden = true;

      fetch(form.action + '.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().then(function (j) {
              return Promise.reject(j);
            });
          }
          return r.json();
        })
        .then(function () {
          return fetch(form.action.replace(/\/add\/?$/, '') + '.js').then(function (r) {
            return r.json();
          });
        })
        .then(function (cart) {
          document.dispatchEvent(
            new CustomEvent('cart:change', { detail: { item_count: cart.item_count } })
          );
          var n = cart.item_count;
          announce('Added to cart — ' + n + (n === 1 ? ' item' : ' items'));
          buttons.forEach(function (b) {
            if (b.disabled) return;
            var original = b.getAttribute('data-text-add') || b.textContent;
            b.textContent = 'Added ✓';
            setTimeout(function () {
              b.textContent = original;
            }, 2000);
          });
        })
        .catch(function (err) {
          if (err && (err.description || err.message)) {
            var message = err.description || err.message;
            if (errorEl) {
              // role=alert announces on its own — a live-region echo would read twice
              errorEl.textContent = message;
              errorEl.hidden = false;
            } else {
              announce(message);
            }
          } else {
            form.submit(); // network failure: fall back to plain POST
          }
        })
        .finally(function () {
          buttons.forEach(function (b) {
            b.classList.remove('is-loading');
          });
        });
    });
  }

  /* ---------- Sticky ATC bar: show once the main button scrolls above view ---------- */
  function initStickyBar(root) {
    var bar = document.querySelector('[data-sticky-atc]');
    var main = root.querySelector('[data-atc-main]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      var show = !e.isIntersecting && e.boundingClientRect.bottom < 0;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    });
    io.observe(main);
  }

  /* ---------- Native reviews: write form + load more via Typesense API ---------- */
  function initReviewsFacade() {
    var el = document.querySelector('[data-reviews-facade]');
    if (!el) return;

    var api = (el.getAttribute('data-reviews-api') || '').replace(/\/$/, '');
    var productId = el.getAttribute('data-reviews-product') || '';
    var shop = el.getAttribute('data-reviews-shop') || (window.Shopify && Shopify.shop) || '';
    var dialog = el.querySelector('[data-reviews-dialog]');
    var form = el.querySelector('[data-reviews-form]');
    var formStatus = el.querySelector('[data-reviews-form-status]');
    var moreBtn = el.querySelector('[data-reviews-more]');
    var list = el.querySelector('[data-reviews-items]');
    var page = 1;
    var loadingMore = false;

    function apiUrl(path, params) {
      var u = new URL(path, api || window.location.origin);
      Object.keys(params).forEach(function (k) {
        if (params[k] != null && params[k] !== '') u.searchParams.set(k, params[k]);
      });
      return u.toString();
    }

    function setFormStatus(msg, isError) {
      if (!formStatus) return;
      formStatus.hidden = !msg;
      formStatus.textContent = msg || '';
      formStatus.classList.toggle('is-error', !!isError);
      announce(msg);
    }

    function cardHtml(item) {
      var name = item.name || '';
      var title = item.title || '';
      var body = item.body || '';
      var date = item.date || '';
      // Coerce here: the API may return numeric ids, and id.replace() below
      // would throw and abort the whole load-more batch.
      var id = String(item.id || Math.random()).slice(0, 64);
      var fill = Math.max(0, Math.min(100, (Number(item.rating) || 0) * 20));
      var photos = (item.photos || [])
        .slice(0, 5)
        .map(function (src) {
          var url = typeof src === 'string' ? src : src && src.url;
          if (!url) return '';
          return (
            '<li><img src="' +
            url.replace(/"/g, '') +
            '" alt="" width="80" height="80" loading="lazy" decoding="async"></li>'
          );
        })
        .join('');
      return (
        '<li><article class="reviews-list__item" data-review-id="' +
        id.replace(/"/g, '') +
        '"><div class="reviews-list__meta">' +
        '<svg viewBox="0 0 120 24" width="80" height="16" aria-hidden="true"><defs>' +
        '<linearGradient id="rlm' +
        id.replace(/[^a-zA-Z0-9_-]/g, '') +
        '" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="120" y2="0">' +
        '<stop offset="' +
        fill +
        '%" stop-color="#fcd00e"/><stop offset="' +
        fill +
        '%" stop-color="#d3d3d3"/></linearGradient></defs>' +
        '<g fill="url(#rlm' +
        id.replace(/[^a-zA-Z0-9_-]/g, '') +
        ')">' +
        '<path transform="translate(0 0)" d="M12 2.2l3 6.1 6.7 1-4.85 4.7 1.15 6.7L12 17.5l-6 3.2 1.15-6.7L2.3 9.3l6.7-1z"/>' +
        '<path transform="translate(24 0)" d="M12 2.2l3 6.1 6.7 1-4.85 4.7 1.15 6.7L12 17.5l-6 3.2 1.15-6.7L2.3 9.3l6.7-1z"/>' +
        '<path transform="translate(48 0)" d="M12 2.2l3 6.1 6.7 1-4.85 4.7 1.15 6.7L12 17.5l-6 3.2 1.15-6.7L2.3 9.3l6.7-1z"/>' +
        '<path transform="translate(72 0)" d="M12 2.2l3 6.1 6.7 1-4.85 4.7 1.15 6.7L12 17.5l-6 3.2 1.15-6.7L2.3 9.3l6.7-1z"/>' +
        '<path transform="translate(96 0)" d="M12 2.2l3 6.1 6.7 1-4.85 4.7 1.15 6.7L12 17.5l-6 3.2 1.15-6.7L2.3 9.3l6.7-1z"/>' +
        '</g></svg>' +
        '<span class="visually-hidden" data-review-rating></span>' +
        (name ? '<strong></strong>' : '') +
        (item.verified ? '<span class="reviews-list__verified"></span>' : '') +
        (date ? '<time></time>' : '') +
        '</div>' +
        (title ? '<h3 class="reviews-list__title"></h3>' : '') +
        (body ? '<p class="reviews-list__body"></p>' : '') +
        (photos ? '<ul class="reviews-list__photos">' + photos + '</ul>' : '') +
        '</article></li>'
      );
    }

    function fillText(root, item) {
      var srRating = root.querySelector('[data-review-rating]');
      if (srRating) srRating.textContent = 'Rated ' + (Number(item.rating) || 0) + ' out of 5 stars';
      var strong = root.querySelector('strong');
      if (strong) strong.textContent = item.name || '';
      var verified = root.querySelector('.reviews-list__verified');
      if (verified) verified.textContent = 'Verified buyer';
      var time = root.querySelector('time');
      if (time && item.date) {
        time.setAttribute('datetime', item.date);
        time.textContent = item.date;
      }
      var h3 = root.querySelector('.reviews-list__title');
      if (h3) h3.textContent = item.title || '';
      var p = root.querySelector('.reviews-list__body');
      if (p) p.textContent = item.body || '';
    }

    function existingIds() {
      var ids = {};
      el.querySelectorAll('[data-review-id]').forEach(function (node) {
        ids[node.getAttribute('data-review-id')] = true;
      });
      return ids;
    }

    function loadMore() {
      if (!api || !productId || loadingMore) return;
      loadingMore = true;
      page += 1;
      if (moreBtn) moreBtn.disabled = true;
      fetch(apiUrl('/api/storefront/reviews', { shop: shop, product_id: productId, page: String(page) }))
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.error || 'Failed');
            return data;
          });
        })
        .then(function (data) {
          var seen = existingIds();
          var items = data.items || [];
          items.forEach(function (item) {
            if (!item || !item.id || seen[item.id] || !list) return;
            list.insertAdjacentHTML('beforeend', cardHtml(item));
            var node = list.lastElementChild;
            if (node) fillText(node, item);
          });
          if (moreBtn && data.has_more === false) moreBtn.hidden = true;
        })
        .catch(function () {
          page -= 1;
        })
        .finally(function () {
          loadingMore = false;
          if (moreBtn) moreBtn.disabled = false;
        });
    }

    if (form) {
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        if (!api) {
          setFormStatus('Reviews are not configured yet.', true);
          return;
        }
        var fd = new FormData(form);
        var payload = {
          name: String(fd.get('name') || ''),
          email: String(fd.get('email') || ''),
          title: String(fd.get('title') || ''),
          body: String(fd.get('body') || ''),
          rating: Number(fd.get('rating') || 0),
          company: String(fd.get('company') || ''),
          product_id: productId
        };
        var submit = form.querySelector('[type="submit"]');
        if (submit) submit.disabled = true;
        setFormStatus('Sending…', false);
        fetch(apiUrl('/api/storefront/reviews', { shop: shop }), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
          .then(function (res) {
            return res.json().then(function (data) {
              if (!res.ok) throw new Error(data.error || 'Failed');
              return data;
            });
          })
          .then(function (data) {
            setFormStatus(data.message || 'Thanks — your review was submitted.', false);
            form.reset();
          })
          .catch(function (err) {
            setFormStatus(err.message || 'Could not submit review.', true);
          })
          .finally(function () {
            if (submit) submit.disabled = false;
          });
      });
    }

    document.addEventListener('click', function (ev) {
      if (ev.target.closest('[data-reviews-write]') && el.contains(ev.target)) {
        ev.preventDefault();
        el.open = true;
        if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
        return;
      }
      if (ev.target.closest('[data-reviews-more]') && el.contains(ev.target)) {
        ev.preventDefault();
        el.open = true;
        loadMore();
        return;
      }
      var link = ev.target.closest('a[href="#reviews"], a[href$="#reviews"]');
      if (!link) return;
      ev.preventDefault();
      if (location.hash !== '#reviews') history.replaceState(null, '', '#reviews');
      el.open = true;
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    });

    if (location.hash === '#reviews') el.open = true;
  }

  /* ---------- Click-to-load video facades (product-content accordions) ---------- */
  function initFacades() {
    document.addEventListener('click', function (ev) {
      var facade = ev.target.closest('[data-video-facade]');
      if (!facade) return;
      var id = facade.getAttribute('data-video-id');
      var host = facade.getAttribute('data-video-host');
      var iframe = document.createElement('iframe');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
      );
      iframe.title = facade.getAttribute('data-video-title') || 'Video';
      iframe.src =
        host === 'vimeo'
          ? 'https://player.vimeo.com/video/' + id + '?autoplay=1'
          : 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0';
      var wrapper = facade.parentElement;
      facade.remove();
      wrapper.appendChild(iframe);
    });
  }

  /* ---------- Related products: one deferred fetch near viewport ---------- */
  function initRecommendations() {
    var el = document.querySelector('[data-recommendations]');
    var url = el && el.getAttribute('data-url');
    if (!el || !url || !window.fetch) return;

    var load = function () {
      fetch(url)
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            if (window.HorizonThumbHash && window.HorizonThumbHash.enhanceImages) {
              window.HorizonThumbHash.enhanceImages(el);
            }
          }
        })
        .catch(function () {});
    };

    if (!('IntersectionObserver' in window)) return load();
    var io = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          io.disconnect();
          load();
        }
      },
      { rootMargin: '400px' }
    );
    io.observe(el);
  }
})();
